/**
 * Intent360 reading tracker.
 *
 * The whole product's credibility rests on this file. If "14 minutes" actually
 * means "left the tab open over lunch", no downstream model can fix it.
 *
 * Rules enforced here:
 *  1. Time only accrues when the document is visible, the window is focused,
 *     and the user has interacted within IDLE_TIMEOUT.
 *  2. Time is attributed to the page currently occupying the most viewport,
 *     measured by IntersectionObserver, not by scroll position guesswork.
 *  3. A page only counts as "read" if dwell >= MIN_DWELL_MS and it was at
 *     least 50% visible. Scrolling past page 12 on the way to page 13 is not
 *     reading page 12.
 *  4. Events are buffered and flushed with sendBeacon so a closing tab still
 *     reports. Every event carries a ULID so the server can dedupe retries.
 */

import { ulid } from './ulid';

const IDLE_TIMEOUT_MS = 30_000;
const HEARTBEAT_MS = 5_000;
const MIN_DWELL_MS = 3_000;
const VISIBILITY_THRESHOLD = 0.5;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER = 40;

export type EventType =
  | 'session_start' | 'page_enter' | 'page_exit' | 'heartbeat'
  | 'scroll_depth' | 'download' | 'print' | 'share' | 'copy'
  | 'search' | 'zoom' | 'session_end';

interface TrackerEvent {
  ulid: string;
  type: EventType;
  ts: number;
  page?: number;
  payload?: Record<string, unknown>;
}

interface PageState {
  enteredAt: number | null;
  activeMs: number;
  maxScroll: number;
  visibleRatio: number;
}

export interface TrackerOptions {
  token: string;
  endpoint: string;
  pageCount: number;
  onScoreHint?: (activeSeconds: number, qualifiedPages: number) => void;
}

export class ReadingTracker {
  private readonly opts: TrackerOptions;
  // A UUID, not a ULID: this id becomes the reading_session primary key, and
  // core stores session_id as a UUID (EventIngestService does
  // UUID.fromString(sessionId)). A ULID here fails the ::uuid cast and every
  // event for the session is dropped. Event ids below stay ULIDs — those are
  // deduped as opaque strings, not parsed as UUIDs.
  private readonly sessionId = crypto.randomUUID();
  private readonly pages = new Map<number, PageState>();
  private buffer: TrackerEvent[] = [];

  private activePage: number | null = null;
  private lastTickAt = 0;
  private lastInteractionAt = Date.now();
  private totalActiveMs = 0;
  private running = false;

  // Interaction counters. Carried (cumulative) in heartbeat + session_end
  // payloads and max-merged server-side, so retries never double-count.
  private clickCount = 0;
  private zoomCount = 0;
  private lastZoom = 1;
  private readonly baseDpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;

  private observer?: IntersectionObserver;
  private tickTimer?: number;
  private flushTimer?: number;

  constructor(opts: TrackerOptions) {
    this.opts = opts;
    for (let p = 1; p <= opts.pageCount; p++) {
      this.pages.set(p, { enteredAt: null, activeMs: 0, maxScroll: 0, visibleRatio: 0 });
    }
  }

  // ---------------------------------------------------------------- lifecycle

  start(container: HTMLElement) {
    if (this.running) return;
    this.running = true;
    this.lastTickAt = Date.now();

    this.emit('session_start', undefined, {
      pageCount: this.opts.pageCount,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      dpr: window.devicePixelRatio,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer || null,
      // Cheap headless tells. The server does the real bot scoring; this is a hint.
      automation: navigator.webdriver === true,
      touch: navigator.maxTouchPoints > 0,
    });

    this.attachVisibilityObserver(container);
    this.attachInteractionListeners();
    this.attachExfilListeners();

    this.tickTimer = window.setInterval(() => this.tick(), 1_000);
    this.flushTimer = window.setInterval(() => this.flush(false), FLUSH_INTERVAL_MS);
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this.settleActivePage();
    this.emit('session_end', undefined, this.summary());
    window.clearInterval(this.tickTimer);
    window.clearInterval(this.flushTimer);
    this.observer?.disconnect();
    this.flush(true);
  }

  // ------------------------------------------------------------- attention

  /** True only when this human could plausibly be reading right now. */
  private isAttentive(): boolean {
    return (
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      Date.now() - this.lastInteractionAt < IDLE_TIMEOUT_MS
    );
  }

  private tick() {
    const now = Date.now();
    const delta = now - this.lastTickAt;
    this.lastTickAt = now;

    // Guard against a laptop waking from sleep and reporting a 4-hour "tick".
    if (delta > HEARTBEAT_MS * 3) return;
    if (!this.isAttentive() || this.activePage === null) return;

    this.totalActiveMs += delta;
    const st = this.pages.get(this.activePage);
    if (st) st.activeMs += delta;

    if (this.totalActiveMs % HEARTBEAT_MS < 1_000) {
      this.emit('heartbeat', this.activePage, {
        activeMs: Math.round(this.totalActiveMs),
        pageActiveMs: Math.round(st?.activeMs ?? 0),
        clicks: this.clickCount,
        zooms: this.zoomCount,
      });
      this.opts.onScoreHint?.(Math.round(this.totalActiveMs / 1000), this.qualifiedPages());
    }
  }

  /**
   * Attribute attention to whichever page owns the most screen. Tracking
   * "current page" from a scrollTop offset breaks on zoom, on two-page spreads,
   * and on continuous scroll — intersection ratio does not.
   */
  private attachVisibilityObserver(container: HTMLElement) {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const page = Number((e.target as HTMLElement).dataset.page);
          const st = this.pages.get(page);
          if (st) st.visibleRatio = e.intersectionRatio;
        }

        let best: number | null = null;
        let bestRatio = VISIBILITY_THRESHOLD;
        for (const [page, st] of this.pages) {
          if (st.visibleRatio > bestRatio) { best = page; bestRatio = st.visibleRatio; }
        }
        if (best !== this.activePage) this.switchPage(best);
      },
      // root: null = the viewport. The pages scroll with the window, not inside
      // an overflow container, so intersection must be measured against the
      // viewport — the `.page { min-height: 70vh }` rule exists for exactly this.
      // With root set to the (non-scrolling) container, ratios never change as
      // the window scrolls, no page switch ever fires, and page_exit — the only
      // event that fills perPage — is never emitted (pages stay 0/0).
      { root: null, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    container.querySelectorAll<HTMLElement>('[data-page]')
      .forEach((el) => this.observer!.observe(el));
  }

  private switchPage(next: number | null) {
    this.settleActivePage();
    this.activePage = next;
    if (next !== null) {
      const st = this.pages.get(next)!;
      st.enteredAt = Date.now();
      this.emit('page_enter', next);
    }
  }

  private settleActivePage() {
    if (this.activePage === null) return;
    const page = this.activePage;
    const st = this.pages.get(page);
    if (!st || st.enteredAt === null) return;

    const dwellMs = Math.round(st.activeMs);
    this.emit('page_exit', page, {
      dwellMs,
      maxScroll: Number(st.maxScroll.toFixed(3)),
      qualified: dwellMs >= MIN_DWELL_MS && st.visibleRatio >= VISIBILITY_THRESHOLD,
    });
    st.enteredAt = null;
  }

  private qualifiedPages(): number {
    let n = 0;
    for (const st of this.pages.values()) if (st.activeMs >= MIN_DWELL_MS) n++;
    return n;
  }

  // ------------------------------------------------------------- listeners

  private attachInteractionListeners() {
    const mark = () => { this.lastInteractionAt = Date.now(); };
    for (const ev of ['mousemove', 'keydown', 'wheel', 'touchstart', 'pointerdown', 'scroll']) {
      window.addEventListener(ev, mark, { passive: true });
    }

    // Discrete clicks — an active, deliberate interaction with the document.
    window.addEventListener('click', () => { this.clickCount++; mark(); }, { passive: true });

    // Zoom — the reader leaning in to examine detail (charts, pricing, fine
    // print). Covers both pinch (visualViewport.scale) and browser ctrl-zoom
    // (devicePixelRatio relative to the initial value).
    const onZoom = () => {
      const scale = (window.visualViewport?.scale ?? 1) * (window.devicePixelRatio / this.baseDpr);
      if (Math.abs(scale - this.lastZoom) > 0.05) { this.lastZoom = scale; this.zoomCount++; mark(); }
    };
    window.addEventListener('resize', onZoom, { passive: true });
    window.visualViewport?.addEventListener('resize', onZoom);

    // Focus/blur bracket the idle window so alt-tabbing stops the clock instantly
    // rather than after the idle timeout expires.
    window.addEventListener('blur', () => { this.lastInteractionAt = 0; });
    window.addEventListener('focus', mark);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { this.settleActivePage(); this.flush(true); }
      else { mark(); this.lastTickAt = Date.now(); }
    });

    let scrollRaf = 0;
    window.addEventListener('scroll', () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        if (this.activePage === null) return;
        const el = document.querySelector<HTMLElement>(`[data-page="${this.activePage}"]`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const seen = Math.min(1, Math.max(0, (window.innerHeight - r.top) / Math.max(r.height, 1)));
        const st = this.pages.get(this.activePage)!;
        if (seen > st.maxScroll + 0.05) {
          st.maxScroll = seen;
          this.emit('scroll_depth', this.activePage, { depth: Number(seen.toFixed(3)) });
        }
      });
    }, { passive: true });
  }

  /** Signals that the reader intends to keep or spread the content. */
  private attachExfilListeners() {
    window.addEventListener('beforeprint', () => this.emit('print', this.activePage ?? undefined));
    document.addEventListener('copy', () => {
      const len = window.getSelection()?.toString().length ?? 0;
      this.emit('copy', this.activePage ?? undefined, { chars: len });
    });
    window.addEventListener('pagehide', () => this.stop());
    window.addEventListener('beforeunload', () => this.stop());
  }

  trackDownload() { this.emit('download', this.activePage ?? undefined); }
  trackShare(channel: string, recipientHash?: string) {
    this.emit('share', this.activePage ?? undefined, { channel, recipientHash });
  }
  trackSearch(term: string) {
    // Search terms are the single highest-signal thing a reader produces.
    this.emit('search', this.activePage ?? undefined, { term: term.slice(0, 120) });
  }

  // --------------------------------------------------------------- transport

  private summary() {
    const perPage: Record<string, { sec: number; scroll: number }> = {};
    for (const [p, st] of this.pages) {
      if (st.activeMs > 0) {
        perPage[p] = { sec: Math.round(st.activeMs / 1000), scroll: Number(st.maxScroll.toFixed(3)) };
      }
    }
    return {
      activeSeconds: Math.round(this.totalActiveMs / 1000),
      qualifiedPages: this.qualifiedPages(),
      pagesTouched: Object.keys(perPage).length,
      clicks: this.clickCount,
      zooms: this.zoomCount,
      perPage,
    };
  }

  private emit(type: EventType, page?: number, payload?: Record<string, unknown>) {
    this.buffer.push({ ulid: ulid(), type, ts: Date.now(), page, payload });
    if (this.buffer.length >= MAX_BUFFER) this.flush(false);
  }

  private flush(final: boolean) {
    if (this.buffer.length === 0) return;
    const batch = this.buffer;
    this.buffer = [];

    const body = JSON.stringify({
      token: this.opts.token,
      sessionId: this.sessionId,
      sentAt: Date.now(),
      events: batch,
    });

    // sendBeacon survives tab close; fetch does not. Always prefer it on exit.
    if (final && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(this.opts.endpoint, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }

    fetch(this.opts.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Put them back; the next flush retries. Server dedupes on ULID.
      this.buffer = batch.concat(this.buffer).slice(-200);
    });
  }
}
